import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  // Animate progress counter
  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      current += Math.floor(Math.random() * 4) + 1;
      if (current >= 100) {
        current = 100;
        clearInterval(interval);
        // Brief pause at 100% then exit
        setTimeout(() => {
          setDone(true);
          setTimeout(() => onComplete?.(), 600);
        }, 400);
      }
      setProgress(current);
    }, 32);
    return () => clearInterval(interval);
  }, [onComplete]);

  const logLines = [
    { prefix: '»', color: '#3b82f6', text: 'Loading core modules...',    delay: 0.3 },
    { prefix: '✓', color: '#4ade80', text: 'Security protocols active',  delay: 0.55 },
    { prefix: '✓', color: '#4ade80', text: 'Cloud services connected',   delay: 0.8  },
    { prefix: '»', color: '#3b82f6', text: 'Launching experience...',    delay: 1.05 },
  ];

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="loader"
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
          style={{ background: '#000000' }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.55, ease: 'easeInOut' } }}
        >
          {/* Grid background */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(59,130,246,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.04) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />

          {/* Ambient glow blobs */}
          <motion.div
            className="absolute -top-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'rgba(37,99,235,0.12)', filter: 'blur(60px)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.div
            className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: 'rgba(124,58,237,0.1)', filter: 'blur(60px)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}
          />

          {/* Scan line sweep */}
          <motion.div
            className="absolute left-0 right-0 h-px pointer-events-none"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.6), transparent)' }}
            initial={{ top: '-2px', opacity: 0.8 }}
            animate={{ top: '102%', opacity: 0 }}
            transition={{ duration: 2.2, delay: 0.5, ease: 'easeInOut' }}
          />

          {/* Corner brackets */}
          {[
            { style: { top: 24, left: 24 },    border: 'borderTop borderLeft'  },
            { style: { top: 24, right: 24 },    border: 'borderTop borderRight' },
            { style: { bottom: 24, left: 24 },  border: 'borderBottom borderLeft'  },
            { style: { bottom: 24, right: 24 }, border: 'borderBottom borderRight' },
          ].map((c, i) => (
            <motion.div
              key={i}
              className="absolute w-7 h-7"
              style={{
                ...c.style,
                borderTop:    c.border.includes('borderTop')    ? '2px solid #3b82f6' : undefined,
                borderBottom: c.border.includes('borderBottom') ? '2px solid #3b82f6' : undefined,
                borderLeft:   c.border.includes('borderLeft')   ? '2px solid #3b82f6' : undefined,
                borderRight:  c.border.includes('borderRight')  ? '2px solid #3b82f6' : undefined,
              }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.3 }}
            />
          ))}

          {/* ── Center content ── */}
          <div className="relative z-10 flex flex-col items-center">

            {/* Spinning logo ring */}
            <div className="relative w-24 h-24 mb-7">
              {/* Outer rotating ring */}
              <motion.svg
                className="absolute inset-0"
                viewBox="0 0 96 96"
                fill="none"
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <circle cx="48" cy="48" r="44" stroke="rgba(59,130,246,0.15)" strokeWidth="1" />
                <circle cx="48" cy="48" r="44" stroke="url(#arcGrad)" strokeWidth="2"
                  strokeDasharray="85 192" strokeLinecap="round" />
                <circle cx="48" cy="4" r="3.5" fill="#3b82f6" />
                <defs>
                  <linearGradient id="arcGrad" x1="0" y1="0" x2="96" y2="96" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#2563eb" />
                    <stop offset="0.5" stopColor="#7c3aed" />
                    <stop offset="1" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </motion.svg>

              {/* Inner circle + CM monogram */}
              <motion.div
                className="absolute inset-3 rounded-full flex items-center justify-center"
                style={{
                  background: '#000',
                  border: '1px solid rgba(59,130,246,0.3)',
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#3b82f6',
                  letterSpacing: '-1px',
                }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.8, type: 'spring', stiffness: 260 }}
              >
                CM
              </motion.div>
            </div>

            {/* Company name — typewriter reveal */}
            <motion.div
              className="text-white font-bold text-3xl tracking-widest uppercase overflow-hidden whitespace-nowrap"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.0 }}
            >
              CmCoreTech
            </motion.div>

            {/* Tagline */}
            <motion.div
              className="text-xs tracking-[0.35em] uppercase mt-1.5"
              style={{ color: 'rgba(59,130,246,0.8)', fontFamily: "'Rajdhani', sans-serif", fontWeight: 300 }}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.7 }}
            >
              Software · Cloud · Cybersecurity
            </motion.div>

            {/* Progress bar */}
            <motion.div
              className="mt-10 w-64"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 1.3 }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] tracking-widest uppercase"
                  style={{ fontFamily: "'Space Mono', monospace", color: 'rgba(255,255,255,0.3)' }}>
                  Initializing
                </span>
                <span className="text-[11px] font-bold"
                  style={{ fontFamily: "'Space Mono', monospace", color: '#3b82f6' }}>
                  {progress}%
                </span>
              </div>
              <div className="w-full h-0.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <motion.div
                  className="h-full rounded-full relative"
                  style={{ background: 'linear-gradient(90deg, #2563eb, #7c3aed, #06b6d4)' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.1, ease: 'linear' }}
                >
                  {/* Glowing tip */}
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-3 rounded-sm opacity-80"
                    style={{ background: '#fff' }} />
                </motion.div>
              </div>
            </motion.div>

            {/* Boot log lines */}
            <div className="mt-5 w-64 space-y-0.5">
              {logLines.map(({ prefix, color, text, delay }, i) => (
                <motion.div
                  key={i}
                  className="text-[9px] tracking-wider"
                  style={{ fontFamily: "'Space Mono', monospace", color: 'rgba(255,255,255,0.25)' }}
                  initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay }}
                >
                  <span style={{ color }}>{prefix} </span>{text}
                </motion.div>
              ))}
            </div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;